// Small status pieces shared by the screens: trip pill, climate pill, avatar with
// sync dot, sync pill, offline banner, tier pill. One sync pattern everywhere (HANDOVER §10).
import { useEffect, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { OrganiserIcon, PlayerFace, SnowflakeIcon } from '../art/icons'
import { useSession } from '../data/session'
import { useSync } from '../data/sync'
import { useLiveWeather } from '../data/weather'
import type { TripClock } from '../domain/tripClock'
import { climateFor } from '../map/bindings'
import type { MotionTier } from '../motion/tier'
import { fmt, t } from '../strings/en'
import { useParentModePill } from './ParentModeClock'
import { openOverlay } from './router'
import { syncedLabel, syncedShort } from './syncLabel'

/**
 * The trip pill, top left on the Map: its one fact (map/journey pillText), "78 days to Sapporo" or
 * "Day 9 · Niseko". Looking at another stop it says "back ↩" and a tap flies back to now
 * (`onBack`; WI-04). In parent mode (WI-17) it becomes the parent-mode pill, "Parent mode · N min ·
 * Done", the same height and yellow in the last minute; it goes back when parent mode ends. With a
 * sheet open (`covered`) it stays the trip pill: the clock is paused then and Settings has its own Done.
 */
export function TripPill({ text, onBack, covered = false }: { text: string; onBack?: () => void; covered?: boolean }) {
  const parent = useParentModePill((s) => s.pill)
  if (parent && !covered) {
    return (
      <span className={`pill parent-pill ${parent.warn ? 'pill-yellow' : 'pill-ink'}`} role="status">
        <span className="parent-pill-text" aria-label={parent.warn ? t.parentMode.warn(parent.child, parent.minutesLeft) : undefined}>
          {t.parentMode.left(parent.minutesLeft)}
        </span>
        <button className="parent-done press" onClick={() => void useSession.getState().backToChild()}>
          {t.parentMode.done}
        </button>
      </span>
    )
  }
  if (onBack) {
    return (
      <button className="pill trip-pill press" onClick={onBack} aria-label={t.map.pill.backLabel(text)}>
        <span className="trip-pill-text">{text}</span>
        <small className="trip-pill-back">{t.map.pill.back}</small>
      </button>
    )
  }
  return (
    <span className="pill trip-pill">
      <span className="trip-pill-text">{text}</span>
    </span>
  )
}

/** Typical weather for where we are (or will be) this month, from the pack. Live weather replaces it later. */
export function ClimatePill({ pack, clock, nowMs }: { pack: Pack; clock: TripClock; nowMs: number }) {
  const date = clock.phase === 'during' ? clock.today : pack.meta.startDate
  const live = useLiveWeather(pack, clock, nowMs)
  const climate = climateFor(pack, clock)
  if (live) {
    return (
      <span className="pill">
        <SnowflakeIcon />
        {t.liveTemp(live.tempC)}
      </span>
    )
  }
  if (!climate) return null
  return (
    <span className="pill">
      <SnowflakeIcon />
      {t.climate(date, climate.tempC)}
    </span>
  )
}

export function MeButton({ person }: { person: Person }) {
  const { state } = useSync()
  const dot = state === 'offline' ? 'var(--ink2)' : state === 'queued' ? 'var(--yellow)' : 'var(--mint)'
  return (
    <button className="me press" aria-label={`Settings (${person.name})`} onClick={() => openOverlay('settings')}>
      {person.role === 'organiser' ? <OrganiserIcon /> : <PlayerFace />}
      <i style={{ background: dot }} />
    </button>
  )
}

const zone = () => Intl.DateTimeFormat().resolvedOptions().timeZone
const hhmm = (ms: number) => fmt.hhmm(ms, zone())

/** The clock, a minute at a time, so "just now" and "yesterday" stay true while a pill is up. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])
  return now
}

export function SyncPill({ always = false }: { always?: boolean }) {
  const { state, queued, lastSyncAt, offlineSince } = useSync()
  const now = Math.max(useMinute(), lastSyncAt ?? 0)
  // On screens, the offline banner says it; the pill only shows offline in Settings.
  if (state === 'offline' && offlineSince && always) return <span className="pill pill-ink">{t.sync.offline(hhmm(offlineSince))}</span>
  if (queued) return <span className="pill pill-yellow">{t.sync.queued(queued)}</span>
  if (!always) return null
  return <span className="pill pill-mint">{lastSyncAt ? syncedLabel(lastSyncAt, now, zone()) : t.sync.never}</span>
}

export function OfflineBanner() {
  const { state, queued, lastSyncAt } = useSync()
  const now = Math.max(useMinute(), lastSyncAt ?? 0)
  if (state !== 'offline') return null
  return (
    <div className="offline-banner" role="status">
      <span className="tag yellow">{t.sync.offlineTag}</span>
      <span>{t.sync.banner(queued, lastSyncAt ? syncedShort(lastSyncAt, now, zone()) : null)}</span>
    </div>
  )
}

/**
 * Calm or Still showing. On the Map's top row (`icon`) it's a small round icon, so the trip pill
 * keeps its words (WI-40 #4); Settings › Motion says it in full.
 */
export function TierPill({ tier, icon = false }: { tier: MotionTier; icon?: boolean }) {
  if (tier === 'full') return null
  const name = tier === 'calm' ? t.tier.calm : t.tier.still
  if (icon) {
    return (
      <span className="pill pill-frost tier-icon" role="img" aria-label={name} title={name}>
        {tier === 'calm' ? '≈' : '❚❚'}
      </span>
    )
  }
  return <span className="pill pill-frost">{name}</span>
}
